import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import {
  createForm,
  updateForm,
  updateFormStatus,
  createField,
  updateField,
  deleteField,
  getAssignments,
  createAssignment,
  deleteAssignment,
} from "@/lib/forms-api";
import { useOrgId } from "@/lib/org-context";
import type { FormStatus, FormType } from "@/types/forms";

export function useFormMutations() {
  const qc = useQueryClient();
  const orgId = useOrgId();

  const invalidateForms = () => qc.invalidateQueries({ queryKey: ["forms"] });
  const invalidateForm = (id: string) =>
    qc.invalidateQueries({ queryKey: ["form", orgId, id] });

  const create = useMutation({
    mutationFn: (data: {
      title: string;
      description?: string;
      type: FormType;
      required?: boolean;
    }) => createForm(data),
    onSuccess: () => invalidateForms(),
  });

  const update = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: { title?: string; description?: string | null; required?: boolean };
    }) => updateForm(id, data),
    onSuccess: (_, vars) => {
      invalidateForms();
      invalidateForm(vars.id);
    },
  });

  const setStatus = useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string;
      status?: FormStatus;
      isArchived?: boolean;
    }) => updateFormStatus(id, data),
    onSuccess: (_, vars) => {
      invalidateForms();
      invalidateForm(vars.id);
    },
  });

  return { create, update, setStatus };
}

export function useFieldMutations(formId: string) {
  const qc = useQueryClient();
  const orgId = useOrgId();
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["form", orgId, formId] });

  const addField = useMutation({
    mutationFn: (data: {
      type: string;
      label: string;
      required?: boolean;
      properties?: string;
      validations?: string;
    }) => createField(formId, data),
    onSuccess: invalidate,
  });

  const editField = useMutation({
    mutationFn: ({
      fieldId,
      data,
    }: {
      fieldId: string;
      data: {
        label?: string;
        required?: boolean;
        properties?: string | null;
        validations?: string | null;
      };
    }) => updateField(formId, fieldId, data),
    onSuccess: invalidate,
  });

  const removeField = useMutation({
    mutationFn: (fieldId: string) => deleteField(formId, fieldId),
    onSuccess: invalidate,
  });

  return { addField, editField, removeField };
}

export function useAssignments(formId: string | undefined) {
  const qc = useQueryClient();
  const orgId = useOrgId();

  const { data, isLoading } = useQuery({
    queryKey: ["assignments", orgId, formId],
    queryFn: () => getAssignments(formId!),
    enabled: !!orgId && !!formId,
  });

  const add = useMutation({
    mutationFn: (data: {
      assignableType: "workspace" | "project" | "task";
      assignableId: string;
    }) => createAssignment(formId!, data),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["assignments", orgId, formId] }),
  });

  const remove = useMutation({
    mutationFn: (assignmentId: string) =>
      deleteAssignment(formId!, assignmentId),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["assignments", orgId, formId] }),
  });

  return {
    assignments: data?.data ?? [],
    loading: isLoading,
    add,
    remove,
  };
}
